// Import react libs
import { useState } from 'react';

// Import style
import {
    ProductFormContainer,
    ProductTitleEditor,
    ProductDescriptionEditor,
    ProductPriceDiv,
    ProductPriceSubDiv,
    ProductPriceEditor,
    ProductOfferCheckbox,
    ButtonSection,
    EditButton,
    SubmitButton,
    DeleteButton,
    Icon,
} from './style';

// Import components
import ProductSlideShow from '../../components/ProductSlideShow';
import ImageEditor from '../ImageEditor';

// Import libs
import { generateUniqueFileName } from '../../utils/libs/generateUniqueFileName';

// Import assets
import { ReactComponent as modifyIcon } from '../../assets/icons/settings.svg';

// Import texts
import productTexts from '../../texts/product';
import errorTexts from '../../texts/error';

// Component to edit product infos
function ProductForm({
    ICON_WIDTH = 30,
    productName = '',
    productDescription = '',
    productImages = null,
    productIsOffer = false,
    productPrice = '',
    onSubmit = null,
    onDelete = null,
    setIsNameEmptyError,
    setIsPriceEmptyError,
    resetErrors = null,
}) {
    // Keep in a state if we are editing photos or not
    const [isEditingImages, setIsEditingImages] = useState(false);

    // Keep in a state the products infos
    const [name, setName] = useState(productName);
    const [description, setDescription] = useState(productDescription);
    const [images, setImages] = useState(productImages);
    const [isOffer, setIsOffer] = useState(productIsOffer);
    const [price, setPrice] = useState(productPrice);

    // Function to add image to image list
    async function addImage(imageFile) {
        // Check the type of the file
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(imageFile.type)) {
            alert(errorTexts.invalidImageTypeError);
            return;
        }

        // Define standart new image id and pos
        let newImageId = -1;
        let newImagePosition = 1;

        if (images && images.length > 0) {
            // If the max number of image is reached
            if (images.length >= 5) {
                alert(errorTexts.tooManyImagesError);
                return;
            }

            // Find the lowest negatif id
            const minNegativeId = images
                .filter((image) => typeof image.id === 'number' && image.id < 0)
                .reduce((min, image) => Math.min(min, image.id), 0);
            newImageId = minNegativeId <= -1 ? minNegativeId - 1 : -1;

            // Get max position
            const maxPosition = images.reduce(
                (max, image) => Math.max(max, image.position ?? 0),
                0,
            );
            newImagePosition = maxPosition + 1;
        }

        // Generate a unique file name
        const newFileName = generateUniqueFileName(imageFile.name);

        // Create the new image object
        const newImage = {
            id: newImageId,
            path: newFileName,
            url: URL.createObjectURL(imageFile),
            file: imageFile,
            position: newImagePosition,
        };

        // Set the image list
        setImages((prev) =>
            prev && prev.length > 0 ? [...prev, newImage] : [newImage],
        );
    }

    // Function to remove an image from the image list
    function removeImage(imageId) {
        // Find the image to remove
        const imageToRemove = images.find((image) => image.id === imageId);
        if (!imageToRemove) {
            // Nothing to remove
            return;
        }

        // Get the image to remove position
        const imageToRemovePosition = imageToRemove.position;

        // Remove the image from the list
        const filteredImages = images.filter((image) => image.id !== imageId);

        if (filteredImages && filteredImages.length > 0) {
            if (typeof imageId === 'number' && imageId < 0) {
                // Reduce the position index if it's supperior to removed image position
                const updatedImages = filteredImages
                    .map((image) => {
                        if (image.position > imageToRemovePosition) {
                            return {
                                ...image,
                                position: image.position - 1,
                            };
                        }
                        return image;
                    })
                    // Reduce the image id among the added images
                    .map((image) => {
                        if (
                            typeof image.id === 'number' &&
                            image.id < imageId
                        ) {
                            return { ...image, id: image.id + 1 };
                        }
                        return image;
                    });

                // Set the image list
                setImages(updatedImages);
                return;
            }

            // Create a new table of image
            const updatedImages = filteredImages.map((image) => {
                // Reduce the position index if its supperior to removed image position
                if (image.position > imageToRemovePosition) {
                    return { ...image, position: image.position - 1 };
                }

                return image;
            });

            // Set the image list
            setImages(updatedImages);
            return;
        }

        // If the is no image anymore
        setImages(null);
    }

    // Function to handle submit preventing empty values errors
    function handleSubmit() {
        // Check if name is empty
        const isNameValid = name.trim() !== '';

        // Check if price is empty or = to 0
        const numericPrice = Number(price);
        const isPriceValid =
            price &&
            !isNaN(numericPrice) &&
            Number.isFinite(numericPrice) &&
            numericPrice > 0;

        // Set the errors if there is one
        setIsNameEmptyError(!isNameValid);
        setIsPriceEmptyError(!isPriceValid);

        // If there is no error call the submit function
        if (isNameValid && isPriceValid) {
            onSubmit(
                name.trim(),
                description.trim(),
                isOffer,
                price,
                images && images.length > 0 ? images : null,
            );
        }
    }

    return (
        <ProductFormContainer
            onSubmit={(e) => {
                e.preventDefault();
            }}
        >
            {isEditingImages && (
                <ImageEditor
                    ICON_WIDTH={ICON_WIDTH}
                    productName={name}
                    images={
                        images && images.length > 0
                            ? images.map((image) => ({
                                  id: image.id,
                                  url: image.url,
                              }))
                            : null
                    }
                    addImage={addImage}
                    removeImage={removeImage}
                    onClose={() => setIsEditingImages(false)}
                />
            )}
            <ProductTitleEditor
                placeholder={productTexts.productTitlePlaceholder}
                value={name}
                onChange={(e) => setName(e.target.value)}
            />
            <EditButton type="button" onClick={() => setIsEditingImages(true)}>
                <Icon as={modifyIcon} width={ICON_WIDTH} />
            </EditButton>
            <ProductSlideShow
                ICON_WIDTH={ICON_WIDTH}
                productName={name}
                images={
                    images && images.length > 0
                        ? images.map((image) => image.url)
                        : null
                }
                offer={isOffer}
            />
            <ProductDescriptionEditor
                placeholder={productTexts.productDescriptionPlaceholder}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
            />
            <ProductPriceDiv>
                <ProductPriceSubDiv left="true">
                    <ProductOfferCheckbox
                        type="checkbox"
                        checked={isOffer}
                        onChange={() => setIsOffer(!isOffer)}
                    />
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                        {' '}
                        {productTexts.offer}
                    </p>
                </ProductPriceSubDiv>
                <ProductPriceSubDiv right="true">
                    <ProductPriceEditor
                        type="number"
                        inputMode="numeric"
                        placeholder={productTexts.productPricePlaceholder}
                        value={price}
                        onChange={(e) => {
                            setPrice(
                                e.target.value
                                    // Replace 0 at the begining of the price
                                    .replace(/^0+(?=\d)/, ''),
                            );
                        }}
                        onKeyDown={(e) => {
                            const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                            ];

                            if (
                                !/^[0-9]$/.test(e.key) && // not a number
                                !allowedKeys.includes(e.key)
                            ) {
                                e.preventDefault();
                            }
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                        }}
                    />
                    <p style={{ whiteSpace: 'pre-wrap' }}> $</p>
                </ProductPriceSubDiv>
            </ProductPriceDiv>
            <ButtonSection>
                {onDelete ? (
                    <>
                        <DeleteButton
                            onClick={() => {
                                resetErrors();
                                onDelete();
                            }}
                        >
                            {productTexts.deleteProduct}
                        </DeleteButton>
                        <SubmitButton
                            onClick={() => {
                                resetErrors();
                                handleSubmit();
                            }}
                        >
                            {productTexts.updateProduct}
                        </SubmitButton>
                    </>
                ) : (
                    <SubmitButton onClick={handleSubmit}>
                        {productTexts.addProduct}
                    </SubmitButton>
                )}
            </ButtonSection>
        </ProductFormContainer>
    );
}

export default ProductForm;
